import { List, useDataGrid } from "@refinedev/mui";
import React from "react";
import { useDeleteMany } from "@refinedev/core";

import { GridRowSelectionModel } from "@mui/x-data-grid";
import { Button } from "@mui/material";

import { ISchedule } from "./interfaces";
import { SearchToolbar, DataGridPanel, ListPageHeader } from "components/list";
import { scheduleColumns } from "./components/scheduleColumns";
import { scheduleSearchFields } from "./components/scheduleSearchFields";
import styles from "./list.module.css";

export const ScheduleList: React.FC = () => {
    const { dataGridProps, setFilters, filters, tableQueryResult } =
        useDataGrid<ISchedule>({
            initialCurrent: 1,
            initialPageSize: 20,
            sorters: {
                initial: [{ field: "id", order: "desc" }],
            },
        });

    const [rowSelectionModel, setRowSelectionModel] =
        React.useState<GridRowSelectionModel>([]);

    // refine의 rowCount는 fetch 중 잠깐 0으로 떨어져 "전체 0건"이 깜빡인다.
    // 로딩이 아닐 때의 마지막 값을 유지해 표시한다.
    const lastRowCountRef = React.useRef<number | undefined>(undefined);
    if (!dataGridProps.loading && typeof dataGridProps.rowCount === "number") {
        lastRowCountRef.current = dataGridProps.rowCount;
    }

    const { mutate } = useDeleteMany<ISchedule>();

    // 선택된 행 일괄 삭제 (현행 로직 유지)
    const deleteSelectedItems = () => {
        mutate(
            {
                resource: "schedules",
                ids: rowSelectionModel.map((id) => id.toString()),
            },
            {
                onSuccess: () => {
                    setRowSelectionModel([]);
                },
            }
        );
    };

    const hasSelected = rowSelectionModel.length > 0;

    return (
        <List
            breadcrumb={<></>}
            headerButtons={({ defaultButtons }) => (
                <>
                    {defaultButtons}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={deleteSelectedItems}
                        disabled={!hasSelected}
                    >
                        선택 삭제하기
                    </Button>
                </>
            )}
        >
            <ListPageHeader
                count={lastRowCountRef.current}
                description="아이러빗 현장요원의 시설 방문 배정 및 수락 현황"
            />

            <SearchToolbar
                fields={scheduleSearchFields}
                filters={filters}
                setFilters={setFilters}
            />

            <DataGridPanel
                className={styles.grid}
                {...dataGridProps}
                columns={scheduleColumns}
                getRowShowPath={(row) => `/schedules/show/${row.id}`}
                isError={tableQueryResult.isError}
                errorMessage="방문일정을 불러오지 못했습니다."
                emptyMessage="조건에 맞는 방문일정이 없습니다."
                pageSizeOptions={[10, 20, 30, 50, 100]}
                disableColumnFilter
                filterModel={undefined}
                checkboxSelection
                onRowSelectionModelChange={(model) => setRowSelectionModel(model)}
                rowSelectionModel={rowSelectionModel}
                // zebra: 가상화와 무관하게 페이지 내 절대 위치 기준으로 클래스 부여
                getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? styles.rowEven : ""
                }
            />
        </List>
    );
};
